import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { api, ResponseValidationError } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { apiErrorMessage, formatApiMessage } from '@/lib/api-error'
import { CAPE_COD_TOWNS } from '@/lib/cape-towns'
import { readInviteError, type OnboardingDeliveryError } from '@/lib/onboarding'

const formSchema = z.object({
  name: z.string().trim().min(1, "Enter the owner's name").max(200),
  email: z.string().trim().email('Enter a valid email address'),
  address: z.string().trim().min(1, 'Enter the street address').max(300),
  town: z.string().trim().min(1, 'Choose the town').max(100),
  notes: z.string().trim().max(2000, 'Keep notes under 2,000 characters'),
})

type FormValues = z.infer<typeof formSchema>

const EMPTY: FormValues = { name: '', email: '', address: '', town: '', notes: '' }

interface StartResult {
  propertyId: string
  email: string
  created: boolean
  inviteSent: boolean
  inviteError: OnboardingDeliveryError | null
}

const CONTROL_CLASS =
  'w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint'

function inviteErrorText(error: OnboardingDeliveryError) {
  const text = formatApiMessage(error.code, error.message, 'The email could not be sent.')
  return error.retry_after_seconds
    ? `${text} Wait ${error.retry_after_seconds} seconds before trying again.`
    : text
}

/**
 * A new property always attempts the email, so "created, not sent, no reason"
 * is a failure whose reason did not reach us (CAP-1484 has `invite_error` in
 * the contracts), never "already received one".
 */
const UNREPORTED_FAILURE: OnboardingDeliveryError = {
  code: 'invite_not_sent',
  message: 'The email was not sent, and admin-api gave no reason.',
}

function ResultView({ result }: { result: StartResult }) {
  const lines: string[] = []
  if (!result.created) {
    lines.push(
      'This owner and address were already onboarding, so nothing new was created. The existing property is unchanged.'
    )
  }
  if (result.inviteSent) {
    lines.push(`The onboarding email went to ${result.email}.`)
  } else if (!result.inviteError) {
    lines.push('No email was sent: the owner already received one.')
  }
  return (
    <div className="space-y-3 text-sm text-text" data-testid="start-onboarding-result">
      {lines.map(line => (
        <p key={line} className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-mint" aria-hidden />
          <span>{line}</span>
        </p>
      ))}
      {result.inviteError && (
        <div
          role="alert"
          className="flex gap-2 rounded-card border border-amber/30 bg-warningBg p-3 text-text"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden />
          <div className="space-y-1">
            <p className="font-medium">The onboarding email could not be sent.</p>
            <p className="text-textMuted">{inviteErrorText(result.inviteError)}</p>
            <p className="text-textMuted">
              {result.created ? 'The property and its profile were created. ' : ''}
              Try the email again once the problem is fixed; nothing is created twice.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface StartOnboardingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Start onboarding" (CAP-1467): creates the local property, its profile and
 * the owner's portal account, and sends the onboarding email, in one call to
 * `POST /admin/onboarding`. The result stays on screen rather than in a toast,
 * because an email that failed to send is something the operator has to act on.
 */
export function StartOnboardingDialog({ open, onOpenChange }: StartOnboardingDialogProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<StartResult | null>(null)
  // The values last sent, so "Try the email again" repeats exactly the same
  // start: admin-api is idempotent on email plus address, and a retyped
  // address with a typo would create a second property instead.
  const [sent, setSent] = useState<FormValues | null>(null)
  const [busy, setBusy] = useState(false)
  // Set synchronously, so a double click cannot send two starts before the
  // re-render that disables the button.
  const inFlight = useRef(false)
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: EMPTY })

  const handleOpenChange = (next: boolean) => {
    // Not while a request is out: its answer would land in a closed dialog
    // and greet the next owner on reopening.
    if (busy) return
    if (!next) {
      form.reset(EMPTY)
      setSubmitError(null)
      setResult(null)
      setSent(null)
    }
    onOpenChange(next)
  }

  /** `previous`: the result a retry repeats, whose outcome it keeps. */
  const submit = async (values: FormValues, previous: StartResult | null = null) => {
    if (inFlight.current) return
    inFlight.current = true
    setSubmitError(null)
    setBusy(true)
    setSent(values)
    const email = values.email
    try {
      // Already trimmed by the form schema.
      const response = await api.startOnboarding({
        name: values.name,
        email,
        address: values.address,
        town: values.town,
        ...(values.notes ? { notes: values.notes } : {}),
      })
      // Same order as the Properties list's links.
      const propertyId = String(response.property.property_id || response.property.id || '')
      // A retry always answers `created: false`; the start it repeats is
      // what created the property.
      const created = previous ? previous.created : response.created
      const inviteError =
        readInviteError(response) ??
        (!response.invite_email_sent && (created || previous) ? UNREPORTED_FAILURE : null)
      setResult({
        propertyId,
        email,
        created,
        inviteSent: response.invite_email_sent,
        inviteError,
      })
      // Not awaited: the result is already on screen, and its buttons should
      // not wait for the list to refresh behind it.
      void queryClient.invalidateQueries({ queryKey: ['properties'] })
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] })
    } catch (err) {
      // After an unreadable answer the earlier result (and its retry button)
      // no longer says what happened, so it goes.
      if (err instanceof ResponseValidationError) setResult(null)
      setSubmitError(
        err instanceof ResponseValidationError
          ? 'Onboarding may have started, but its answer could not be read. Check Properties before trying again.'
          : apiErrorMessage(err, 'Could not start onboarding. Please try again.')
      )
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{result ? 'Onboarding started' : 'Start onboarding'}</DialogTitle>
          <DialogDescription>
            {result
              ? 'The property is listed under Properties and Onboarding until it is linked to its Uplisting listing.'
              : 'Use this once an owner has signed. It creates the property and its profile, gives the owner a portal account and emails them a link to fill in the profile.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <>
            <ResultView result={result} />
            {submitError && (
              <p role="alert" className="text-sm text-danger">
                {submitError}
              </p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
                Close
              </Button>
              {result.inviteError && sent && (
                <Button variant="outline" onClick={() => void submit(sent, result)} disabled={busy}>
                  {busy ? 'Sending...' : 'Try the email again'}
                </Button>
              )}
              {result.propertyId && (
                <Button
                  disabled={busy}
                  onClick={() => {
                    handleOpenChange(false)
                    navigate(`/properties/${encodeURIComponent(result.propertyId)}`)
                  }}
                >
                  Open property
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <Form {...form}>
            <form
              onSubmit={event => void form.handleSubmit(values => submit(values))(event)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner name</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="name@example.com"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property address</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Street and number" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="town"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Town</FormLabel>
                    <FormControl>
                      <select {...field} className={`${CONTROL_CLASS} h-10`}>
                        <option value="">Choose the town</option>
                        {CAPE_COD_TOWNS.map(town => (
                          <option key={town} value={town}>
                            {town}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (optional)</FormLabel>
                    <FormControl>
                      <textarea {...field} rows={3} className={`${CONTROL_CLASS} resize-y`} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError && (
                <p role="alert" className="text-sm text-danger">
                  {submitError}
                </p>
              )}

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? 'Starting...' : 'Start onboarding'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
